import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/Input';
import TextArea from '../../../components/TextArea';
import { HtmlInputEvent } from '../../../utils/types/HtmlnputEvent';
import {
    ContactUsFormData,
    ContactUsPurposes,
} from '../types/ContactUsFormData';
import { FilesUploader } from './FilesUploader';
import { SelectPurpose } from './SelectPurpose';

interface ContactUsFormProps {
    onSubmit: (data: ContactUsFormData) => void;
}

const OPTIONAL_FIELDS: (keyof ContactUsFormData)[] = ['attachments'];

const defaultFormData: ContactUsFormData = {
    name: '',
    email: '',
    message: '',
    purpose: undefined,
    attachments: undefined,
};

export const ContactUsForm: React.FC<ContactUsFormProps> = ({ onSubmit }) => {
    const [formData, setFormData] =
        useState<ContactUsFormData>(defaultFormData);

    const { t } = useTranslation();

    const handleChange = (e: HtmlInputEvent) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const isDisabled = formIsDisabled(formData);

    const handleSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            if (isDisabled) return;
            onSubmit(formData);
        },
        [isDisabled, formData]
    );

    return (
        <form onSubmit={handleSubmit}>
            <Input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                data-testid={'name-input'}
                label={t('Name')}
            />
            <Input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                data-testid={'email-input'}
                label={t('Email')}
            />
            <SelectPurpose
                changeAction={(value?: ContactUsPurposes) => {
                    setFormData({
                        ...formData,
                        purpose: value,
                    });
                }}
            />
            <TextArea
                name="message"
                required
                value={formData.message}
                onChange={handleChange}
                data-testid={'message-input'}
                label={t('Message')}
                rows={10}
            />
            <FilesUploader
                testid={'file-upload-input'}
                handleChange={(files) => {
                    setFormData({
                        ...formData,
                        attachments: [
                            ...(formData.attachments ?? []),
                            ...files,
                        ],
                    });
                }}
            />
            <button
                className={`btn btn-primary btn-lg btn-block ${isDisabled ? 'disabled' : ''}`}
                data-testid="submit-button"
                type="submit"
            >
                {t('Send')}
            </button>
        </form>
    );
};

function formIsDisabled(formData: ContactUsFormData) {
    return Object.keys(formData).some((key) => {
        const formValue = formData[key as keyof ContactUsFormData];
        const isOptional =
            OPTIONAL_FIELDS.find((value) => value === key) !== undefined;
        if (isOptional) return;
        return formValue === undefined || formValue === '';
    });
}
